import { useState } from "react";

import { Loader2, Sparkles, Eye, Save } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

type ContentType = "board_pack" | "convention_pack" | "session_framework";

interface GeneratedContent {
  name: string;
  description: string;
  category: string;
  data: any;
}

export const ContentCreationManager = () => {
  const { execute: invoke, isLoading: isGenerating } = useEdgeFunction({ auth: true });
  const [contentType, setContentType] = useState<ContentType>("convention_pack");
  const [category, setCategory] = useState("");
  const [prompt, setPrompt] = useState("");
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const categoryOptions = {
    board_pack: ["Technology", "Healthcare", "Finance", "Education", "Marketing", "Operations"],
    convention_pack: ["writing", "code_style", "documentation", "testing", "architecture", "security"],
    session_framework: ["problem-solving", "strategy", "brainstorming", "decision-making", "planning", "review"]
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || !category) {
      toast.error("Please select a category and provide a prompt");
      return;
    }

    const { data, error } = await invoke("generate-content-with-context", {
      body: {
        contentType,
        category,
        prompt: prompt.trim()
      },
      showErrorToast: true,
    });

    if (error) return;

    setGeneratedContent(data.content);
    setShowPreview(true);
    toast.success("Content generated - review before saving");
  };

  const handleSave = async () => {
    if (!generatedContent) return;

    setIsSaving(true);
    try {
      let table: string;
      let insertData: any;

      switch (contentType) {
        case "board_pack":
          table = "prebuilt_boards";
          insertData = {
            name: generatedContent.name,
            description: generatedContent.description,
            personas: generatedContent.data.personas,
            status: "draft"
          };
          break;
        case "convention_pack":
          table = "convention_packs";
          insertData = {
            name: generatedContent.name,
            description: generatedContent.description,
            category: generatedContent.category,
            conventions: generatedContent.data.conventions,
            is_system: false
          };
          break;
        case "session_framework":
          table = "session_frameworks";
          insertData = {
            name: generatedContent.name,
            description: generatedContent.description,
            category: generatedContent.category,
            content: generatedContent.data.content,
            status: "draft"
          };
          break;
      }

      const { error } = await supabase.from(table as any).insert(insertData);

      if (error) throw error;

      toast.success(`${getContentTypeLabel()} created successfully`);

      setShowPreview(false);
      setGeneratedContent(null);
      setPrompt("");
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save content");
    } finally {
      setIsSaving(false);
    }
  };

  const getContentTypeLabel = () => {
    switch (contentType) {
      case "board_pack": return "Board Pack";
      case "convention_pack": return "Convention Pack";
      case "session_framework": return "Session Framework";
    }
  };

  const renderPreviewContent = () => {
    if (!generatedContent) return null;

    switch (contentType) {
      case "board_pack":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Personas</Label>
              <div className="mt-2 space-y-2">
                {generatedContent.data.personas?.map((persona: any, idx: number) => (
                  <Card key={idx}>
                    <CardContent className="pt-4">
                      <h4 className="font-semibold">{persona.name}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{persona.role}</p>
                      <p className="text-sm mt-2">{persona.expertise}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        );
      case "convention_pack":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Conventions</Label>
              <div className="mt-2 space-y-2">
                {generatedContent.data.conventions?.map((conv: any, idx: number) => (
                  <Card key={idx}>
                    <CardContent className="pt-4">
                      <h4 className="font-semibold">{conv.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{conv.description}</p>
                      {conv.example && (
                        <pre className="text-xs mt-2 p-2 bg-muted rounded">{conv.example}</pre>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        );
      case "session_framework":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Framework Content</Label>
              <Card className="mt-2">
                <CardContent className="pt-4">
                  <pre className="text-sm whitespace-pre-wrap">{generatedContent.data.content}</pre>
                </CardContent>
              </Card>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle>AI Content Creator</CardTitle>
          </div>
          <CardDescription>
            Generate new content with AI assistance, informed by existing content to explore new directions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Content Type</Label>
            <Select value={contentType} onValueChange={(val) => setContentType(val as ContentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="board_pack">Board Pack</SelectItem>
                <SelectItem value="convention_pack">Convention Pack</SelectItem>
                <SelectItem value="session_framework">Session Framework</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions[contentType].map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Generation Prompt</Label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what you want to create. Be specific about the focus, target audience, or unique aspects you want to explore..."
              className="min-h-[120px] mt-2"
            />
            <p className="text-xs text-muted-foreground mt-2">
              The AI will consider existing content to ensure your new {getContentTypeLabel().toLowerCase()} explores fresh territory.
            </p>
          </div>

          <Button 
            onClick={handleGenerate} 
            disabled={isGenerating || !category || !prompt.trim()}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating with AI...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Content
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Preview Generated {getContentTypeLabel()}
            </DialogTitle>
          </DialogHeader>

          {generatedContent && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">Name</Label>
                <p className="text-lg font-medium mt-1">{generatedContent.name}</p>
              </div>

              <div>
                <Label className="text-sm font-semibold">Description</Label>
                <p className="text-sm text-muted-foreground mt-1">{generatedContent.description}</p>
              </div>

              <div>
                <Label className="text-sm font-semibold">Category</Label>
                <div className="mt-1">
                  <Badge>{generatedContent.category}</Badge>
                </div>
              </div>

              {renderPreviewContent()}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Content
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
